// pages/Dimensions.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  Button,
  LinearProgress,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import {
  Search,
  TrendingUp,
  Assessment,
  ShowChart,
  Timeline,
  Psychology,
  AutoGraph,
  FilterList,
  Sort,
  Info,
  Warning,
  CheckCircle,
  ArrowForward,
  Download,
  Share,
  CompareArrows,
  Score
} from '@mui/icons-material';
import { useIEP } from '../contexts/IEPContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';

const Dimensions = () => {
  const { dimensions, organization, iepEngine, maturityScore } = useIEP();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedDimension, setSelectedDimension] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [dimensionScores, setDimensionScores] = useState({});
  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedForComparison, setSelectedForComparison] = useState([]);

  const categories = [
    'all',
    'strategic',
    'operational',
    'innovation',
    'financial',
    'customer',
    'employee',
    'technology',
    'sustainability',
    'risk',
    'learning',
    'ecosystem',
    'adaptive',
    'ethical',
    'transcendent',
    'cultural',
    'governance'
  ];

  useEffect(() => {
    if (organization && dimensions) {
      // Generate sample dimension scores
      const scores = {};
      Object.keys(dimensions).forEach(dimId => {
        const dimension = dimensions[dimId];
        const baseScore = Math.floor(Math.random() * 40) + 30; // 30-70
        const improvement = dimension.weight * 100;
        scores[dimId] = {
          current: baseScore,
          target: Math.min(100, baseScore + improvement),
          trend: Math.random() > 0.5 ? 'up' : 'down'
        };
      });
      setDimensionScores(scores);
    }
  }, [organization, dimensions]);

  const filteredDimensions = Object.values(dimensions).filter(dimension => {
    if (searchTerm && !dimension.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !dimension.category.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (filterCategory !== 'all' && dimension.category !== filterCategory) {
      return false;
    }
    return true;
  });

  const handleDimensionClick = (dimension) => {
    setSelectedDimension(dimension);
    setDetailOpen(true);
  };

  const handleCompareToggle = (dimId) => {
    setSelectedForComparison(prev => 
      prev.includes(dimId)
        ? prev.filter(id => id !== dimId)
        : [...prev, dimId]
    );
  };

  const generateRadarData = () => {
    if (!selectedForComparison.length) return [];
    
    const data = categories
      .filter(cat => cat !== 'all')
      .map(category => {
        const categoryDims = filteredDimensions.filter(d => d.category === category);
        const avgScore = categoryDims.length > 0 
          ? categoryDims.reduce((sum, d) => sum + (dimensionScores[d.id]?.current || 50), 0) / categoryDims.length
          : 50;
        
        return {
          category: category.charAt(0).toUpperCase() + category.slice(1),
          score: Math.round(avgScore)
        };
      });
    
    return data;
  };

  const exportDimensions = () => {
    const data = filteredDimensions.map(d => ({
      Name: d.name,
      Category: d.category,
      Weight: d.weight,
      'Current Score': dimensionScores[d.id]?.current || 'N/A',
      'Target Score': dimensionScores[d.id]?.target || 'N/A'
    }));
    
    const csv = [
      Object.keys(data[0]).join(','),
      ...data.map(row => Object.values(row).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dimensions-report.csv';
    a.click();
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h1" gutterBottom>
          Performance Dimensions
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Monitor 56 key dimensions that define organizational excellence
        </Typography>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} mb={4}>
        <Grid item xs={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="primary.main">
              56
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Dimensions
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">
              {maturityScore?.overallScore || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Avg. Dimension Score
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">
              17
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Categories
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="secondary.main">
              Q*
            </Typography>
            <Typography variant="body2" color="text.secondary">
              AI-Optimized
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Controls */}
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search dimensions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filterCategory}
                  label="Category"
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  {categories.map(category => (
                    <MenuItem key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant={comparisonMode ? "contained" : "outlined"}
                startIcon={<CompareArrows />}
                onClick={() => setComparisonMode(!comparisonMode)}
              >
                Compare {selectedForComparison.length > 0 && `(${selectedForComparison.length})`}
              </Button>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={exportDimensions}
                >
                  Export
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<Psychology />}
                  href="/ai-optimizer"
                >
                  AI Analysis
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs 
        value={activeTab} 
        onChange={(e, v) => setActiveTab(v)}
        sx={{ mb: 3 }}
      >
        <Tab label="All Dimensions" />
        <Tab label="By Category" />
        <Tab label="Score Trends" />
        <Tab label="Gap Analysis" />
      </Tabs>

      {/* Comparison Mode */}
      {comparisonMode && selectedForComparison.length > 0 && (
        <Card elevation={3} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <CompareArrows sx={{ verticalAlign: 'middle', mr: 1 }} />
              Dimension Comparison
            </Typography>
            
            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
              {selectedForComparison.map(dimId => {
                const dim = dimensions[dimId];
                const score = dimensionScores[dimId];
                return (
                  <Chip 
                    key={dimId}
                    label={`${dim.name}: ${score?.current || 'N/A'}`}
                    onDelete={() => handleCompareToggle(dimId)}
                    color="primary"
                  />
                );
              })}
            </Box>
            
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={generateRadarData()}>
                <PolarGrid />
                <PolarAngleAxis dataKey="category" />
                <PolarRadiusAxis angle={30} domain={[0, 100]} />
                {selectedForComparison.map((dimId, index) => {
                  const dim = dimensions[dimId];
                  const color = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'][index];
                  return (
                    <Radar
                      key={dimId}
                      name={dim.name}
                      dataKey="score"
                      stroke={color}
                      fill={color}
                      fillOpacity={0.3}
                    />
                  );
                })}
                <Legend />
                <RechartsTooltip />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Dimensions Grid */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {filteredDimensions.map((dimension) => {
            const score = dimensionScores[dimension.id];
            const progress = score ? (score.current / score.target) * 100 : 0;
            
            return (
              <Grid item xs={12} md={6} lg={4} key={dimension.id}>
                <Card 
                  elevation={2}
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    border: selectedForComparison.includes(dimension.id) ? '2px solid #6366f1' : 'none',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 8
                    }
                  }}
                  onClick={() => handleDimensionClick(dimension)}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {dimension.name}
                        </Typography>
                        <Chip 
                          label={dimension.category}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                      {comparisonMode && (
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCompareToggle(dimension.id);
                          }}
                          color={selectedForComparison.includes(dimension.id) ? "primary" : "default"}
                        >
                          <CompareArrows />
                        </IconButton>
                      )}
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {dimension.description?.substring(0, 120)}...
                    </Typography>
                    
                    {/* Score Progress */}
                    <Box mb={2}>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">
                          Current: <strong>{score?.current || 'N/A'}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Target: <strong>{score?.target || 'N/A'}</strong>
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={progress}
                        sx={{ height: 8, borderRadius: 4 }}
                        color={progress >= 80 ? 'success' : progress >= 50 ? 'warning' : 'error'}
                      />
                    </Box>
                    
                    {/* Indicators */}
                    <Box mb={2}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Key Indicators ({dimension.indicators?.length || 0}):
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                        {dimension.indicators?.slice(0, 3).map((indicator, idx) => (
                          <Chip 
                            key={idx}
                            label={indicator.name}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                        {dimension.indicators?.length > 3 && (
                          <Chip 
                            label={`+${dimension.indicators.length - 3}`}
                            size="small"
                          />
                        )}
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        Weight: <strong>{dimension.weight?.toFixed(3)}</strong>
                      </Typography>
                      <Button 
                        size="small" 
                        variant="text"
                        endIcon={<ArrowForward />}
                      >
                        View Details
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Category View */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          {categories.filter(cat => cat !== 'all').map(category => {
            const categoryDims = filteredDimensions.filter(d => d.category === category);
            if (categoryDims.length === 0) return null;
            
            const avgScore = categoryDims.reduce((sum, d) => sum + (dimensionScores[d.id]?.current || 50), 0) / categoryDims.length;
            
            return (
              <Grid item xs={12} md={6} key={category}>
                <Card elevation={3}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </Typography>
                      <Chip 
                        label={`Avg: ${Math.round(avgScore)}`}
                        color={avgScore >= 70 ? 'success' : avgScore >= 50 ? 'warning' : 'error'}
                      />
                    </Box>
                    
                    <LinearProgress 
                      variant="determinate" 
                      value={avgScore}
                      sx={{ height: 8, borderRadius: 4, mb: 2 }}
                      color={avgScore >= 70 ? 'success' : avgScore >= 50 ? 'warning' : 'error'}
                    />
                    
                    <List dense>
                      {categoryDims.slice(0, 5).map(dimension => {
                        const score = dimensionScores[dimension.id];
                        return (
                          <ListItem key={dimension.id} button onClick={() => handleDimensionClick(dimension)}>
                            <ListItemIcon>
                              {score?.trend === 'up' ? (
                                <TrendingUp color="success" />
                              ) : score?.trend === 'down' ? (
                                <TrendingUp color="error" sx={{ transform: 'rotate(180deg)' }} />
                              ) : (
                                <Timeline color="warning" />
                              )}
                            </ListItemIcon>
                            <ListItemText 
                              primary={dimension.name}
                              secondary={`Score: ${score?.current || 'N/A'} (Target: ${score?.target || 'N/A'})`}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                    
                    {categoryDims.length > 5 && (
                      <Button 
                        fullWidth 
                        size="small" 
                        sx={{ mt: 1 }}
                        onClick={() => {
                          setFilterCategory(category);
                          setActiveTab(0);
                        }}
                      >
                        View All {categoryDims.length} Dimensions
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Score Trends View */}
      {activeTab === 2 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Dimension Score Trends
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={filteredDimensions.slice(0, 20).map(d => ({
                  name: d.name,
                  score: dimensionScores[d.id]?.current || 50,
                  target: dimensionScores[d.id]?.target || 80,
                  category: d.category
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="score" fill="#6366f1" name="Current Score" />
                <Bar dataKey="target" fill="#10b981" name="Target Score" opacity={0.6} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Gap Analysis View */}
      {activeTab === 3 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Gap Analysis
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Dimension</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Current Score</TableCell>
                    <TableCell>Target Score</TableCell>
                    <TableCell>Gap</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDimensions
                    .map(dimension => {
                      const score = dimensionScores[dimension.id];
                      const gap = score ? score.target - score.current : 0;
                      const priority = gap > 30 ? 'High' : gap > 15 ? 'Medium' : 'Low';
                      
                      return {
                        dimension,
                        score,
                        gap,
                        priority
                      };
                    })
                    .sort((a, b) => b.gap - a.gap)
                    .slice(0, 10)
                    .map(({ dimension, score, gap, priority }) => (
                      <TableRow key={dimension.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {dimension.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={dimension.category} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {score?.current || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {score?.target || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(100, (score?.current || 0) + gap)}
                              sx={{ width: 60, height: 6, mr: 1 }}
                              color={gap > 30 ? 'error' : gap > 15 ? 'warning' : 'success'}
                            />
                            <Typography variant="body2">
                              {gap}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={priority}
                            size="small"
                            color={priority === 'High' ? 'error' : priority === 'Medium' ? 'warning' : 'success'}
                          />
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="small" 
                            variant="text"
                            onClick={() => handleDimensionClick(dimension)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  }
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Dimension Detail Dialog */}
      <Dialog 
        open={detailOpen} 
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedDimension && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center">
                <Assessment sx={{ mr: 2, color: 'primary.main' }} />
                {selectedDimension.name}
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle1" gutterBottom color="primary">
                Description:
              </Typography>
              <Typography paragraph>
                {selectedDimension.description}
              </Typography>
              
              <Grid container spacing={2} mt={2}>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Category
                    </Typography>
                    <Typography variant="h6">
                      {selectedDimension.category}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Weight
                    </Typography>
                    <Typography variant="h6">
                      {selectedDimension.weight?.toFixed(3)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
              
              {selectedDimension.indicators && (
                <Box mt={3}>
                  <Typography variant="subtitle1" gutterBottom color="primary">
                    Key Indicators:
                  </Typography>
                  <List>
                    {selectedDimension.indicators.map((indicator, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon>
                          <CheckCircle color="success" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={indicator.name}
                          secondary={`${indicator.description} | Target: ${indicator.target} ${indicator.unit}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              
              {selectedDimension.transformationRelationships && (
                <Box mt={3}>
                  <Typography variant="subtitle1" gutterBottom color="primary">
                    Related Transformations:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {selectedDimension.transformationRelationships.map((transId, idx) => (
                      <Chip 
                        key={idx}
                        label={transId}
                        clickable
                        onClick={() => {
                          setDetailOpen(false);
                          window.location.href = `/transformations#${transId}`;
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailOpen(false)}>
                Close
              </Button>
              <Button 
                variant="contained" 
                onClick={() => {
                  setDetailOpen(false);
                  setComparisonMode(true);
                  handleCompareToggle(selectedDimension.id);
                }}
              >
                Compare
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Dimensions;